import { useState, type ReactNode } from 'react';

/**
 * Shared collapsed-by-default group toggle (originally `HabitListScreen.tsx`'s
 * own local component, extracted per `objectives.md` v2.0 §1.4/§1's explicit
 * instruction: "the identical `CollapsibleGroup` pattern `HabitListScreen.tsx`
 * already ships... reused, not reinvented, so the two 'all my things' screens
 * in the product read as siblings"). Used by the habit list's Paused/Archived
 * groups AND the objectives overview's Archived section (both phone and
 * desktop trees) — genuinely the same component, not a per-screen
 * reimplementation.
 *
 * `{label} ({open ? 'hide' : 'show'})` toggle label, `aria-expanded`,
 * collapsed by default — matches the exact pattern this was extracted from.
 */
export function CollapsibleGroup({
  label,
  defaultOpen = false,
  children,
}: {
  readonly label: string;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          fontSize: 'var(--type-label-size)',
          lineHeight: 'var(--type-label-line)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          fontWeight: 600,
          minHeight: 'var(--touch-target-min)',
        }}
      >
        {label} ({open ? 'hide' : 'show'})
      </button>
      {open && <div style={{ marginTop: 'var(--space-2)' }}>{children}</div>}
    </div>
  );
}
